import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import ProfessionPicker from '@/components/ProfessionPicker';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { updateStory } from '@/services/storyService';
import { getMyStoryForEdit, listStoryTypes } from '@/services/storyRepo';
import {
  STORY_WRITE_LOADING_MESSAGES,
  STORY_WRITE_LOADING_INTERVAL_MS,
  STORY_WRITE_LOADING_HINT,
} from '@/lib/storyFormLoadingMessages';

const FALLBACK_TYPE = {
  id: 'personal',
  display_name: 'Transformation story',
  section_1_label: 'Before',
  section_2_label: 'Struggle',
  section_3_label: 'Turning point',
  section_4_label: 'What helped',
  section_5_label: 'Outcome',
  section_count: 5,
};

const sectionLabelsOf = (t) => [
  t.section_1_label, t.section_2_label, t.section_3_label,
  t.section_4_label, t.section_5_label,
].slice(0, t.section_count || 5);

const storyUsesStructuredBody = (s) => (s?.sections?.length ?? 0) > 0;

const EditStoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [story, setStory] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessageIdx, setSaveMessageIdx] = useState(0);

  const [title, setTitle] = useState('');
  const [rawText, setRawText] = useState('');
  const [sections, setSections] = useState({
    section_1: '', section_2: '', section_3: '', section_4: '', section_5: '',
  });
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [professions, setProfessions] = useState([]);

  const [storyTypes, setStoryTypes] = useState([FALLBACK_TYPE]);

  const userId = user?.id;
  const useStructured = story ? storyUsesStructuredBody(story) : false;
  const exitEditPath = story?.status === 'approved' && id ? `/story/${id}` : '/my-stories';
  const exitEditLabel = story?.status === 'approved' ? 'Back to story' : 'Back to my stories';

  const selectedType = useMemo(
    () => storyTypes.find((t) => t.id === (story?.story_type || 'personal')) || FALLBACK_TYPE,
    [storyTypes, story?.story_type],
  );
  const sectionLabels = useMemo(() => sectionLabelsOf(selectedType), [selectedType]);

  useEffect(() => {
    let cancelled = false;
    listStoryTypes()
      .then((types) => {
        if (cancelled || !types?.length) return;
        setStoryTypes(types);
      })
      .catch((err) => console.error('Failed to load story types:', err));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!authLoading && !userId) {
      toast.error('Please sign in to edit a story');
      navigate('/login');
    }
  }, [userId, authLoading, navigate, id]);

  useEffect(() => {
    if (!saving) {
      setSaveMessageIdx(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setSaveMessageIdx((prev) => (prev + 1) % STORY_WRITE_LOADING_MESSAGES.length);
    }, STORY_WRITE_LOADING_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [saving]);

  useEffect(() => {
    if (!id || authLoading || !userId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setAccessDenied(false);
      try {
        const fetched = await getMyStoryForEdit(id);
        if (cancelled) return;
        if (!fetched) {
          setAccessDenied(true);
          toast.error('Story not found or you do not have access');
          navigate('/my-stories', { replace: true });
          return;
        }
        setStory(fetched);
        setTitle(fetched.title || '');
        setRawText(fetched.raw_text || '');
        setSections({
          section_1: fetched.story?.before || '',
          section_2: fetched.story?.struggle || '',
          section_3: fetched.story?.turning_point || '',
          section_4: fetched.story?.what_helped || '',
          section_5: fetched.story?.outcome || '',
        });
        setIsAnonymous(!!fetched.is_anonymous);
        setProfessions(Array.isArray(fetched.professions) ? fetched.professions : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Edit story load error:', err);
        setLoadError(err?.message || 'Could not load this story');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, userId, authLoading, navigate]);

  const updateSection = (key, value) => {
    setSections((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !id) return;

    if (useStructured) {
      const anyFilled = Object.values(sections).some((s) => s && s.trim());
      if (!anyFilled) {
        toast.error('Fill at least one section before saving');
        return;
      }
      if (!title.trim()) {
        toast.error('Please keep a short title');
        return;
      }
    } else {
      if (!rawText.trim() || rawText.trim().length < 50) {
        toast.error('Write at least a few sentences (50+ characters)');
        return;
      }
    }

    setSaving(true);
    try {
      const updates = {
        is_anonymous: isAnonymous,
        author_display_name: isAnonymous ? null : (user?.name || null),
        professions,
      };
      if (title.trim()) updates.title = title.trim();

      if (useStructured) {
        for (let i = 1; i <= 5; i += 1) {
          const k = `section_${i}`;
          updates[k] = (sections[k] || '').trim();
        }
      } else {
        updates.raw_text = rawText.trim();
      }

      await updateStory(id, updates);
      toast.success('Story updated.');
      navigate(story?.status === 'approved' ? `/story/${id}` : '/my-stories');
    } catch (error) {
      console.error('Error saving story:', error);
      toast.error(error.message || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
          <p className="mt-4 text-[#6B665E]">Loading editor…</p>
        </div>
      </div>
    );
  }

  if (accessDenied) return null;

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-2xl text-[#2D2A26] font-serif">Could not open the editor</p>
          <p className="text-sm text-[#6B665E] mt-2 break-words">{loadError}</p>
          <Button
            onClick={() => navigate('/my-stories')}
            className="mt-6 bg-[#C05621] hover:bg-[#A0461A] text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to my stories
          </Button>
        </div>
      </div>
    );
  }

  if (!story) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <PageHeader>
        <ToolbarCaptionButton
          label={exitEditLabel}
          shortLabel={story?.status === 'approved' ? 'Story' : 'Stories'}
          onClick={() => navigate(exitEditPath)}
          data-testid="edit-back-to-story-btn"
        />
      </PageHeader>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {['rejected', 'needs_revision', 'pending'].includes(story.status) &&
        (story.moderator_notes ||
          (story.reader_report_count != null && story.reader_report_count > 0)) ? (
          <div
            className="mb-8 rounded-xl border border-amber-200 bg-amber-50/90 px-5 py-4 text-sm text-[#2D2A26]"
            data-testid="edit-moderation-feedback"
          >
            <p className="font-medium text-[#2D2A26] mb-1">From moderators</p>
            {story.reader_report_count != null && story.reader_report_count > 0 ? (
              <p className="text-[#6B665E] mb-2">
                Reader reports received (lifetime):{' '}
                <strong className="text-[#2D2A26]">{story.reader_report_count}</strong>
                . Individual reporters stay private.
              </p>
            ) : null}
            {story.moderator_notes ? (
              <p className="text-[#2D2A26] whitespace-pre-wrap leading-relaxed">{story.moderator_notes}</p>
            ) : (
              <p className="text-[#6B665E]">No moderator message on file.</p>
            )}
          </div>
        ) : null}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-[#2D2A26] mb-4">
            Edit your story
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E]">
            {useStructured
              ? 'Update your sections below. Tags and AI metadata stay as-is unless you change the text (search embedding refreshes automatically).'
              : 'Update your text below. Saving refreshes search when the body changes.'}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/story/${id}/review`)}
            className="mt-4 text-sm font-medium text-[#C05621] hover:text-[#A0461A] underline-offset-2 hover:underline"
            data-testid="edit-open-review-btn"
          >
            Edit themes & highlight →
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="story-edit-form">
          <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-2">
            <h3 className="text-lg font-serif font-medium text-[#2D2A26]">Story type</h3>
            <p className="text-sm text-[#6B665E]">
              {selectedType.display_name}
              {' '}
              <span className="text-[#6B665E]/80">({story.story_type || 'personal'})</span>
            </p>
            <p className="text-xs text-[#6B665E]">Type cannot be changed after publish; contact support if you need a different shape.</p>
          </div>

          <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-6">
            <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">Basics</h3>

            <div>
              <Label htmlFor="edit-title">Story Title{useStructured ? ' *' : ''}</Label>
              <Input
                id="edit-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short title"
                required={useStructured}
                data-testid="edit-story-title-input"
                className="mt-2"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-anonymous"
                checked={isAnonymous}
                onCheckedChange={(checked) => setIsAnonymous(!!checked)}
                data-testid="edit-anonymous-toggle"
              />
              <Label htmlFor="edit-anonymous" className="cursor-pointer">
                Share anonymously
              </Label>
            </div>

            <ProfessionPicker value={professions} onChange={setProfessions} />
          </div>

          {!useStructured && (
            <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-4" data-testid="edit-freeflow-block">
              <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">Your story</h3>
              <Textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="Your narrative…"
                required
                data-testid="edit-story-rawtext-input"
                className="min-h-64"
              />
            </div>
          )}

          {useStructured && (
            <div className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-6" data-testid="edit-structured-block">
              <h3 className="text-2xl font-serif font-medium text-[#2D2A26]">Sections</h3>
              {sectionLabels.map((label, idx) => {
                const key = `section_${idx + 1}`;
                return (
                  <div key={key}>
                    <Label htmlFor={key}>{label}</Label>
                    <Textarea
                      id={key}
                      value={sections[key]}
                      onChange={(e) => updateSection(key, e.target.value)}
                      placeholder={`${label}…`}
                      data-testid={`edit-story-${key}-input`}
                      className="mt-2 min-h-32"
                    />
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(exitEditPath)}
              className="sm:flex-1 border-[#E6E2D6]"
              data-testid="edit-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="sm:flex-1 rounded-full px-8 py-6 text-lg font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-lg hover:shadow-xl"
              data-testid="edit-save-btn"
            >
              {saving ? STORY_WRITE_LOADING_MESSAGES[saveMessageIdx] : 'Save changes'}
            </Button>
          </div>
          {saving && (
            <p className="text-sm text-[#6B665E] text-center -mt-4" data-testid="edit-loading-hint">
              {STORY_WRITE_LOADING_HINT}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditStoryPage;
